// Galvanism power (amoeba-powers.md, Phase C): a periodic static discharge
// that chains through nearby predators — targets hit per discharge = power
// level. The lethal counterpart to Sporulation's shrink: chip damage, kills
// drop DNA. Cluster-punisher; value scales with how much you let them clump.

import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { EventBus } from '../events'
import { powerLevel, POWER_DEFS } from '../powers'
import { nearestPredator } from './targeting'
import { dropDnaForPredator } from './damage'
import { spawnInkSplatter, spawnPigmentPuff } from './particles'
import { playSfx, panFor } from '../../engine/audio'
import { T } from '../tunables'

export function galvanismSystem(world: GameWorld, player: Entity, dt: number, events: EventBus): void {
  const c = T.powers.galvanism
  const level = powerLevel(player, 'galvanism')

  player.bioArcCooldown = Math.max(0, (player.bioArcCooldown ?? 0) - dt)
  if (level === 0 || player.bioArcCooldown > 0) return

  const first = nearestPredator(world, player.position.x, player.position.y, c.range)
  if (!first) return

  player.bioArcCooldown = Math.max(0.6, c.intervalBase - c.intervalPerLevel * (level - 1))
  playSfx('lunge_tick', { volume: 0.25, pan: panFor(first.position.x, player.position.x) })

  const hit = new Set<Entity>()
  const tint = POWER_DEFS['galvanism'].tint
  let from: Entity = player
  let target: Entity | null = first

  while (target && hit.size < level) {
    hit.add(target)
    // Bolt visual: a jagged 2-kink path (not a straight stipple) — points
    // dense along each segment, ink-blue outer + white-hot core.
    {
      const fx = from.position.x, fy = from.position.y
      const txp = target.position.x, typ = target.position.y
      const dx = txp - fx, dy = typ - fy
      const len = Math.hypot(dx, dy) || 1
      const px = -dy / len, py = dx / len
      const k1 = (Math.random() - 0.5) * 0.5 * len
      const k2 = (Math.random() - 0.5) * 0.5 * len
      const pts = [
        [fx, fy],
        [fx + dx * 0.33 + px * k1, fy + dy * 0.33 + py * k1],
        [fx + dx * 0.66 + px * k2, fy + dy * 0.66 + py * k2],
        [txp, typ],
      ]
      for (let sgi = 0; sgi < 3; sgi++) {
        const [ax, ay] = pts[sgi]
        const [bx2, by2] = pts[sgi + 1]
        for (let j = 0; j <= 2; j++) {
          const tt = j / 2
          const x = ax + (bx2 - ax) * tt
          const y = ay + (by2 - ay) * tt
          spawnPigmentPuff(world, x, y, tint, 0.030)
          spawnPigmentPuff(world, x, y, [1.0, 1.0, 0.95], 0.016)
        }
      }
    }
    target.health!.current -= c.damage
    target.hitFlash = T.damage.hitFlashDuration
    events.emit({ type: 'damage.dealt', target, amount: c.damage })
    spawnInkSplatter(world, target.position.x, target.position.y)

    from = target
    target = nearestPredator(world, from.position.x, from.position.y, c.chainRange, hit)
  }

  for (const pred of hit) {
    if (pred.health!.current <= 0) {
      dropDnaForPredator(world, pred)
      if (world.id(pred) !== undefined) world.remove(pred)
    }
  }
}
